'use client';

import { useEffect, useRef } from 'react';

/**
 * Magnetic hover: while the pointer is over the element, the element is pulled
 * toward the cursor (dampened), then springs back on leave. A classic
 * award-site microinteraction for primary CTAs.
 *
 * Disabled for reduced-motion and non-fine pointers (touch). GSAP is
 * dynamic-imported so it never lands in a page's entry chunk.
 */
export function useMagnetic<T extends HTMLElement>(strength = 0.35) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const fine = window.matchMedia?.('(pointer: fine)').matches;
    if (reduced || !fine) return;

    let active = true;
    let cleanup = () => {};

    (async () => {
      const { gsap } = await import('@/lib/gsap');
      if (!active || !ref.current) return;
      const node = ref.current;
      const xTo = gsap.quickTo(node, 'x', { duration: 0.4, ease: 'power3.out' });
      const yTo = gsap.quickTo(node, 'y', { duration: 0.4, ease: 'power3.out' });

      const onMove = (e: MouseEvent) => {
        const rect = node.getBoundingClientRect();
        const relX = e.clientX - (rect.left + rect.width / 2);
        const relY = e.clientY - (rect.top + rect.height / 2);
        xTo(relX * strength);
        yTo(relY * strength);
      };
      const onLeave = () => {
        xTo(0);
        yTo(0);
      };

      node.addEventListener('mousemove', onMove);
      node.addEventListener('mouseleave', onLeave);
      cleanup = () => {
        node.removeEventListener('mousemove', onMove);
        node.removeEventListener('mouseleave', onLeave);
        gsap.set(node, { x: 0, y: 0 });
      };
    })();

    return () => {
      active = false;
      cleanup();
    };
  }, [strength]);

  return ref;
}
